import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Trophy, Search, Award } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface Organizer {
  id: string;
  name: string;
  rating: number;
  eventsPerYear: number;
  avatar?: string;
  upcomingEvents: number;
  sports: string[];
}

interface ExploreOrganizersProps {
  organizers: Organizer[];
  onOrganizerClick: (organizer: Organizer) => void;
}

const ExploreOrganizers = ({ organizers, onOrganizerClick }: ExploreOrganizersProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSport, setSelectedSport] = useState("todos");
  const isMobile = useIsMobile();

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  // Normalize sport names to avoid duplicates (Padel, padel, Pádel -> Pádel)
  const normalizeSportName = (sport: string): string => {
    const normalized = sport.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (normalized === 'padel') return 'Pádel';
    if (normalized === 'tenis') return 'Tenis';
    if (normalized === 'pickleball') return 'Pickleball';
    return sport;
  };

  // Filter organizers based on search and sport
  const filteredOrganizers = organizers.filter(organizer => {
    const matchesSearch = organizer.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSport = selectedSport === "todos" || organizer.sports.some(sport => 
      normalizeSportName(sport).toLowerCase() === selectedSport.toLowerCase()
    );
    return matchesSearch && matchesSport;
  });

  // Fixed list of available sports - always show all options
  const availableSports = ["todos", "Pádel", "Tenis", "Pickleball"];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar organizadores..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 placeholder:text-xs placeholder:text-gray-400"
                />
              </div>
            </div>
            
            {/* Sport Filter */}
            <div className="flex gap-2 justify-center">
              {availableSports.map((sport) => (
                <Button
                  key={sport}
                  variant={selectedSport === sport ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedSport(sport)}
                  className="capitalize text-xs md:text-sm h-6 md:h-8 px-2 md:px-4"
                >
                  {sport === "todos" ? "Todos" : sport}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="bg-white">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {filteredOrganizers.map((organizer) => (
              <Card 
                key={organizer.id} 
                className="hover:shadow-lg transition-shadow duration-200 bg-white border-gray-200 cursor-pointer"
                onClick={() => onOrganizerClick(organizer)}
              >
                <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
                  <div className={`flex ${isMobile ? 'items-center gap-2' : 'items-start gap-4'}`}>
                    {/* Avatar */}
                    <div className={`${isMobile ? 'w-10 h-10' : 'w-16 h-16'} bg-primary rounded-full flex items-center justify-center text-primary-foreground ${isMobile ? 'text-sm' : 'text-lg'} font-bold shrink-0 overflow-hidden`}>
                      {organizer.avatar ? (
                        <img src={organizer.avatar} alt={organizer.name} className="w-full h-full object-cover" />
                      ) : (
                        getInitials(organizer.name)
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-gray-900 truncate`}>
                        {organizer.name}
                      </h3>
                      <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
                        <Trophy className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1 shrink-0`} />
                        <span>{organizer.eventsPerYear} eventos realizados</span>
                      </div>
                      <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>
                        <Calendar className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1 shrink-0`} />
                        <span>{organizer.upcomingEvents} próximos eventos</span>
                      </div>
                    </div>
                    
                    {/* Button */}
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        size={isMobile ? "xs" : "sm"}
                        className="bg-primary hover:bg-primary/90"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOrganizerClick(organizer);
                        }}
                      >
                        Ver perfil
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredOrganizers.length === 0 && (
              <div className="text-center py-8">
                <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron organizadores</h3>
                <p className="text-gray-600">Intenta ajustar tus filtros de búsqueda</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExploreOrganizers;
